import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PLATEAU_PAR_DEFAUT } from "@homecad/schema";
import { KernelClient } from "../kernel/client.js";
import type { EvaluationResult, ReglagesDebit, SourceChargee } from "../kernel/protocol.js";
import { Scene } from "../viewport/scene.js";
import { Compte } from "../auth/Compte.js";
import { nomSansExtension, telecharger } from "../parts/echange.js";

/**
 * La station de decoupe : on depose une piece dessinee ailleurs, on regle le
 * plateau, on recupere les morceaux.
 *
 * Rien n'est enregistre, et c'est voulu. Une piece se redessine dans FreeCAD a
 * chaque correction ; la garder ici en ferait une copie perimee des le premier
 * changement. Ce qui merite d'etre garde — le fichier source — est deja range
 * la ou il a ete dessine.
 *
 * Le solide, lui, ne quitte jamais le worker : une forme OpenCascade n'existe
 * que dans la memoire WebAssembly ou elle a ete construite. Cet ecran ne detient
 * que des reglages et des maillages.
 */

const REGLAGES_INITIAUX: ReglagesDebit = {
  plateau: [PLATEAU_PAR_DEFAUT, PLATEAU_PAR_DEFAUT, PLATEAU_PAR_DEFAUT],
  marge: 10,
  liaisons: "tourillons",
  diametre: 5,
  longueur: 30,
  jeu: 0.4,
  paroi: 2,
};

/** Le temps qu'on laisse a une saisie avant de relancer un debit d'une seconde. */
const REPOS_SAISIE = 400;

export function Station(): JSX.Element {
  const kernel = useMemo(() => new KernelClient(), []);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<Scene>();

  const [pret, setPret] = useState(false);
  const [source, setSource] = useState<SourceChargee>();
  const [reglages, setReglages] = useState<ReglagesDebit>(REGLAGES_INITIAUX);
  const [result, setResult] = useState<EvaluationResult>();
  const [travail, setTravail] = useState<string>();
  const [erreur, setErreur] = useState<string>();
  const [eclatement, setEclatement] = useState(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (canvas === null || container === null) return;
    const scene = new Scene(canvas, container);
    sceneRef.current = scene;
    return () => {
      scene.dispose();
      sceneRef.current = undefined;
    };
  }, []);

  useEffect(() => {
    kernel
      .init()
      .then(() => setPret(true))
      .catch((cause: unknown) => setErreur(message(cause)));
    return () => kernel.terminate();
  }, [kernel]);

  const charger = useCallback(
    async (fichier: File) => {
      setErreur(undefined);
      setTravail("Lecture du fichier…");
      setResult(undefined);
      try {
        const chargee = await kernel.charger(fichier.name, await fichier.arrayBuffer());
        setSource(chargee);
      } catch (cause) {
        setSource(undefined);
        setErreur(message(cause));
      } finally {
        setTravail(undefined);
      }
    },
    [kernel],
  );

  /**
   * Le debit suit les reglages, apres un temps de repos. Sans lui, chaque
   * frappe dans un champ lancerait une decoupe d'une seconde ; avec lui, on
   * n'en lance qu'une, celle qui compte. Les reponses en retard sont ecartees
   * par `isCurrent` — on ne peint pas un etat perime par-dessus le bon.
   */
  useEffect(() => {
    if (source === undefined) return;
    let annule = false;
    const minuterie = setTimeout(() => {
      setTravail("Découpe…");
      setErreur(undefined);
      kernel
        .debiter(reglages)
        .then(({ id, result: debit }) => {
          if (annule || !kernel.isCurrent("debiter", id)) return;
          setResult(debit);
          sceneRef.current?.setBodies(debit.bodies);
          sceneRef.current?.frameAll();
        })
        .catch((cause: unknown) => {
          if (annule) return;
          setResult(undefined);
          sceneRef.current?.setBodies([]);
          setErreur(message(cause));
        })
        .finally(() => {
          if (!annule) setTravail(undefined);
        });
    }, REPOS_SAISIE);
    return () => {
      annule = true;
      clearTimeout(minuterie);
    };
  }, [kernel, source, reglages]);

  const corps = result?.bodies ?? [];

  const exporter = useCallback(async () => {
    if (source === undefined) return;
    setTravail("Export…");
    setErreur(undefined);
    try {
      const base = nomSansExtension(source.nom.replace(/\.(step|stp)$/i, ""));
      for (const [rang, morceau] of corps.entries()) {
        const data = await kernel.exporterMorceau(morceau.bodyId);
        telecharger(
          new Blob([data], { type: "model/stl" }),
          `${base}-${String(rang + 1).padStart(2, "0")}-${nomSansExtension(morceau.name)}.stl`,
        );
      }
    } catch (cause) {
      setErreur(message(cause));
    } finally {
      setTravail(undefined);
    }
  }, [corps, kernel, source]);

  /** Ecrit un reglage. Type par champ, pour qu'une faute de frappe ne compile pas. */
  const poser = <C extends keyof ReglagesDebit>(champ: C, valeur: ReglagesDebit[C]): void =>
    setReglages((courant) => ({ ...courant, [champ]: valeur }));

  return (
    <div className="station">
      {/* Le titre et le compte tiennent sur toute la largeur, au-dessus des deux
          colonnes : ce ne sont pas des reglages de decoupe, ils n'ont rien a
          faire dans le panneau qui en est plein. */}
      <header className="bandeau">
        <div className="marque">
          <h1>HomeCAD</h1>
          <span className="sous-titre">Station de découpe</span>
        </div>
        <Compte />
      </header>

      <div className="app">
        <aside className="panneau">
          <h2>Pièce</h2>
          <Depot pret={pret} charger={(f) => void charger(f)} />
          {source !== undefined && (
            <p className="meta">
              {source.nom} · {formaterEncombrement(source.boundingBox)} ·{" "}
              {(source.volume / 1000).toFixed(1)} cm³
            </p>
          )}

          <h2>Plateau</h2>
          <div className="champ-ligne vecteur">
            <span>Volume imprimable</span>
            <div className="vec3">
              {reglages.plateau.map((valeur, axe) => (
                <input
                  key={axe}
                  type="number"
                  min={1}
                  value={valeur}
                  title={`Plateau ${"XYZ"[axe]}`}
                  onChange={(event) => {
                    const suivant = [...reglages.plateau] as [number, number, number];
                    suivant[axe] = Number(event.target.value);
                    poser("plateau", suivant);
                  }}
                />
              ))}
            </div>
          </div>
          <Nombre label="Marge de plateau" valeur={reglages.marge} poser={(v) => poser("marge", v)} />

          <h2>Liaisons</h2>
          <label className="champ-ligne">
            <span>Entre les morceaux</span>
            <select
              value={reglages.liaisons}
              onChange={(event) =>
                poser("liaisons", event.target.value === "aucune" ? "aucune" : "tourillons")
              }
            >
              <option value="tourillons">Tourillons</option>
              <option value="aucune">Aucune</option>
            </select>
          </label>
          {reglages.liaisons === "tourillons" && (
            <>
              <Nombre label="Tourillon ⌀" valeur={reglages.diametre} poser={(v) => poser("diametre", v)} />
              <Nombre label="Tourillon longueur" valeur={reglages.longueur} poser={(v) => poser("longueur", v)} />
              <Nombre label="Tourillon jeu" valeur={reglages.jeu} pas={0.05} poser={(v) => poser("jeu", v)} />
              <Nombre label="Paroi mini autour" valeur={reglages.paroi} poser={(v) => poser("paroi", v)} />
            </>
          )}

          <h2>Export</h2>
          <button
            type="button"
            onClick={() => void exporter()}
            disabled={corps.length === 0 || travail !== undefined}
          >
            {corps.length > 1
              ? `Exporter les ${corps.length} pièces séparément`
              : "Exporter en STL"}
          </button>

          {erreur !== undefined && (
            <p className="erreur" role="alert">
              {erreur}
            </p>
          )}
          {(result?.avertissements.length ?? 0) > 0 && (
            <ul className="avertissements">
              {result?.avertissements.map((texte, rang) => (
                <li key={rang}>{texte}</li>
              ))}
            </ul>
          )}
        </aside>

        <div className="viewport" ref={containerRef}>
          <canvas ref={canvasRef} />
          {corps.length > 1 && (
            <label className="eclatement">
              <span>Vue éclatée</span>
              <input
                type="range"
                min={0}
                max={100}
                step={1}
                value={Math.round(eclatement * 100)}
                onChange={(event) => {
                  const valeur = Number(event.target.value) / 100;
                  setEclatement(valeur);
                  sceneRef.current?.eclater(valeur);
                }}
              />
              <span className="valeur">{Math.round(eclatement * 100)} %</span>
            </label>
          )}
          <div className="etat">
            {travail !== undefined ? (
              travail
            ) : !pret ? (
              <>Chargement d'OpenCascade…</>
            ) : source === undefined ? (
              <>Aucune pièce chargée.</>
            ) : (
              <>
                {corps.length} corps · {result?.elapsedMs.toFixed(0) ?? "—"} ms
                {corps.map((morceau) => (
                  <div key={morceau.bodyId}>
                    {morceau.name} · {(morceau.volume / 1000).toFixed(1)} cm³ ·{" "}
                    {formaterEncombrement(morceau.boundingBox)}
                  </div>
                ))}
              </>
            )}
            </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Le depot de fichier. Glisser-deposer ET bouton : le premier est le geste
 * naturel quand on sort d'un export FreeCAD, le second reste le seul qui marche
 * au clavier.
 */
function Depot({ pret, charger }: { pret: boolean; charger: (f: File) => void }): JSX.Element {
  const [survol, setSurvol] = useState(false);
  const entree = useRef<HTMLInputElement>(null);

  const premier = (fichiers: FileList | null): void => {
    const fichier = fichiers?.[0];
    if (fichier !== undefined) charger(fichier);
  };

  return (
    <div
      className={`depot${survol ? " survol" : ""}${pret ? "" : " inerte"}`}
      onDragOver={(event) => {
        event.preventDefault();
        setSurvol(true);
      }}
      onDragLeave={() => setSurvol(false)}
      onDrop={(event) => {
        event.preventDefault();
        setSurvol(false);
        if (pret) premier(event.dataTransfer.files);
      }}
    >
      <p>Déposez un fichier STEP</p>
      <p className="note">exporté depuis FreeCAD, en millimètres</p>
      <button type="button" disabled={!pret} onClick={() => entree.current?.click()}>
        Choisir un fichier
      </button>
      <input
        ref={entree}
        type="file"
        accept=".step,.stp,model/step"
        hidden
        onChange={(event) => {
          premier(event.target.files);
          // Sans cela, recharger DEUX FOIS le meme fichier ne declenche rien :
          // la valeur n'a pas change, donc l'evenement ne part pas.
          event.target.value = "";
        }}
      />
    </div>
  );
}

function Nombre({
  label,
  valeur,
  pas = 0.5,
  poser,
}: {
  label: string;
  valeur: number;
  pas?: number;
  poser: (valeur: number) => void;
}): JSX.Element {
  return (
    <label className="champ-ligne">
      <span>{label}</span>
      <input
        type="number"
        step={pas}
        min={0}
        value={valeur}
        onChange={(event) => poser(Number(event.target.value))}
      />
    </label>
  );
}

function message(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function formaterEncombrement(box: {
  min: [number, number, number];
  max: [number, number, number];
}): string {
  return box.max.map((valeur, index) => (valeur - box.min[index]!).toFixed(1)).join(" × ") + " mm";
}
